/**
 * CR320: S1.2 Diagnostics Dashboard Data Hook
 * 
 * Polls flyer_scan_runs_v2 and ocr_page_lifecycle for live S1.2 diagnostics.
 * Computes health classification and debug bundle generation.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";

// ============================================================================
// TYPES
// ============================================================================

export type S12Stage =
  | "PAGE_START"
  | "FETCH_PAGE_IMAGE"
  | "OCR_REQUEST"
  | "OCR_PARSE"
  | "SIGNAL_SCORE"
  | "PAGE_DONE";

export type HealthLevel = "healthy" | "warn" | "stuck";

export type PageStatus = "DONE" | "PROCESSING" | "ERROR" | "SKIPPED" | "DEFERRED" | "PENDING";

export interface S12RunData {
  id: string;
  status: string;
  current_page: number | null;
  pages_total: number | null;
  current_phase: string | null;
  last_progress_at: string | null;
  watchdog_timeout_sec: number;
  started_at: string | null;
  completed_at: string | null;
  failure_code: string | null;
  failure_reason: string | null;
  force_stop_requested: boolean;
  force_skip_page_requested: number | null;
  stop_requested_at: string | null;
  skip_requested_at: string | null;
  primary_ocr_engine: string | null;
  pages_rendered: number | null;
  pages_ocr_completed: number | null;
  pages_candidate: number | null;
  pages_ai_scanned: number | null;
  deals_detected_count: number | null;
  discovery_debug: any;
}

export interface PageLifecycleRow {
  id: string;
  page_num: number;
  attempt_num: number;
  profile: string;
  ocr_engine: string;
  status: string;
  started_at: string;
  ended_at: string | null;
  duration_ms: number | null;
  render_ms: number | null;
  preprocess_ms: number | null;
  ocr_ms: number | null;
  postprocess_ms: number | null;
  text_len: number | null;
  avg_conf: number | null;
  error_code: string | null;
  error_message: string | null;
  det_boxes: number | null;
  width: number | null;
  height: number | null;
}

export interface S12DebugEvent {
  time: string;
  page: number | null;
  stage: string;
  message: string;
  ms?: number;
}

export interface S12DiagnosticsState {
  runData: S12RunData | null;
  pages: PageLifecycleRow[];
  events: S12DebugEvent[];
  resolvedRunId: string | null;
  resolvedBy: "direct" | "attempt_primary" | "attempt_link_from_run_id" | "attempt_fallback" | "batch_active_safe" | "none";
  health: HealthLevel;
  healthMessage: string;
  noProgressSeconds: number;
  isLoading: boolean;
  lastFetchedAt: string | null;
}

// ============================================================================
// HEALTH CLASSIFICATION
// ============================================================================

function classifyHealth(
  runData: S12RunData | null,
  noProgressSec: number,
  isRunningHint: boolean
): { level: HealthLevel; message: string } {
  const normalizedStatus = String(runData?.status ?? "").toLowerCase();
  if (!runData) {
    if (isRunningHint) {
      return { level: "warn", message: "Running, but telemetry run is not linked yet (attempt is active)." };
    }
    return { level: "healthy", message: "Not running" };
  }

  if (normalizedStatus !== "running" && normalizedStatus !== "in_progress") {
    if (isRunningHint) {
      return { level: "warn", message: "Running (status sync pending…)" };
    }
    return { level: "healthy", message: "Not running" };
  }

  const watchdog = runData.watchdog_timeout_sec || 300;
  const warnThreshold = Math.max(watchdog * 0.5, 60);
  const stuckThreshold = Math.max(watchdog, 120);

  const stage = runData.current_phase || "unknown";
  const page = runData.current_page ?? "?";
  const total = runData.pages_total ?? "?";

  if (noProgressSec >= stuckThreshold) {
    const suggest =
      stage === "OCR_REQUEST" || stage === "FETCH_PAGE_IMAGE"
        ? "Try Skip Page or switch OCR engine"
        : "Force stop and investigate";
    return {
      level: "stuck",
      message: `Stuck ${formatDurationShort(noProgressSec)} • Stage: ${stage} • Page ${page}/${total} • Suggest: "${suggest}"`,
    };
  }

  if (noProgressSec >= warnThreshold) {
    const suggest =
      stage === "FETCH_PAGE_IMAGE"
        ? "Try Skip Page"
        : stage === "OCR_REQUEST"
        ? "OCR timeout → Skip page or switch engine"
        : "Monitor closely";
    return {
      level: "warn",
      message: `No progress ${formatDurationShort(noProgressSec)} • Stage: ${stage} • Page ${page}/${total} • Suggest: "${suggest}"`,
    };
  }

  return {
    level: "healthy",
    message: `Healthy • Stage: ${stage} • Page ${page}/${total} • Last progress ${noProgressSec}s ago`,
  };
}

function formatDurationShort(sec: number): string {
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

// ============================================================================
// EVENTS EXTRACTION
// ============================================================================

function extractEventsFromDebug(debug: any): S12DebugEvent[] {
  if (!debug?.s12?.events) return [];
  const raw = debug.s12.events;
  if (!Array.isArray(raw)) return [];
  return raw.slice(-50).map((e: any) => ({
    time: e.time || e.timestamp || "",
    page: e.page ?? null,
    stage: e.stage || "",
    message: e.message || e.msg || "",
    ms: e.ms ?? e.duration_ms ?? undefined,
  }));
}

// ============================================================================
// HOOK
// ============================================================================

export function useS12DiagnosticsData(
  flyerScanRunId: string | null | undefined,
  isRunning: boolean,
  batchRunId?: string,
  s1OcrAttemptId?: string | null,
  attemptStartedAt?: string | null,
) {
  const [state, setState] = useState<S12DiagnosticsState>({
    runData: null,
    pages: [],
    events: [],
    resolvedRunId: null,
    resolvedBy: "none",
    health: "healthy",
    healthMessage: "Loading…",
    noProgressSeconds: 0,
    isLoading: true,
    lastFetchedAt: null,
  });

  const fetchData = useCallback(async () => {
    if (!flyerScanRunId && !batchRunId && !s1OcrAttemptId) return;

    try {
      let runData: S12RunData | null = null;
      let resolvedRunId: string | null = null;
      let resolvedBy: "direct" | "attempt_primary" | "attempt_link_from_run_id" | "attempt_fallback" | "batch_active_safe" | "none" = "none";

      // Always prefer run rows linked to the active OCR step attempt id.
      if (!runData && s1OcrAttemptId) {
        const { data: attemptBoundPrimary } = await supabase
          .from("flyer_scan_runs_v2")
          .select("*")
          .eq("discovery_debug->>s1_ocr_attempt_id", s1OcrAttemptId)
          .order("created_at", { ascending: false })
          .limit(1);
        const attemptRun = (attemptBoundPrimary?.[0] as S12RunData | undefined) ?? null;
        if (attemptRun) {
          runData = attemptRun;
          resolvedRunId = attemptRun.id;
          resolvedBy = "attempt_primary";
        }
      }

      if (!runData && flyerScanRunId) {
        const { data: directRun } = await supabase
          .from("flyer_scan_runs_v2")
          .select("*")
          .eq("id", flyerScanRunId)
          .maybeSingle();
        if (directRun) {
          runData = directRun as S12RunData;
          resolvedRunId = runData.id;
          resolvedBy = "direct";
        }
      }

      // CR646: flyer_scan_run_id in step diagnostics can be the S1_OCR attempt id
      // (canonical lifecycle id), not flyer_scan_runs_v2.id. Resolve by attempt link.
      if (!runData && flyerScanRunId) {
        const { data: linkedFromRunId } = await supabase
          .from("flyer_scan_runs_v2")
          .select("*")
          .eq("discovery_debug->>s1_ocr_attempt_id", flyerScanRunId)
          .order("created_at", { ascending: false })
          .limit(1);
        const linkedRun = (linkedFromRunId?.[0] as S12RunData | undefined) ?? null;
        if (linkedRun) {
          runData = linkedRun;
          resolvedRunId = linkedRun.id;
          resolvedBy = "attempt_link_from_run_id";
        }
      }

      if (!runData && s1OcrAttemptId) {
        const { data: attemptBoundFallback } = await supabase
          .from("flyer_scan_runs_v2")
          .select("*")
          .eq("discovery_debug->>s1_ocr_attempt_id", s1OcrAttemptId)
          .order("created_at", { ascending: false })
          .limit(1);
        const attemptRun = (attemptBoundFallback?.[0] as S12RunData | undefined) ?? null;
        if (attemptRun) {
          runData = attemptRun;
          resolvedRunId = attemptRun.id;
          resolvedBy = "attempt_fallback";
        }
      }

      // CR646: Safe fallback if attempt link is missing (older deployments).
      // Only choose active batch run rows created near this attempt start.
      if (!runData && batchRunId && isRunning && attemptStartedAt) {
        const attemptStartMs = new Date(attemptStartedAt).getTime();
        const lowerBoundIso = new Date(attemptStartMs - 10 * 60 * 1000).toISOString();
        const { data: activeBatchRows } = await supabase
          .from("flyer_scan_runs_v2")
          .select("*")
          .eq("discovery_debug->>cr075_batch_run_id", batchRunId)
          .in("status", ["IN_PROGRESS", "RUNNING", "QUEUED"])
          .gte("created_at", lowerBoundIso)
          .order("created_at", { ascending: false })
          .limit(5);
        const safeRun = (activeBatchRows?.[0] as S12RunData | undefined) ?? null;
        if (safeRun) {
          runData = safeRun;
          resolvedRunId = safeRun.id;
          resolvedBy = "batch_active_safe";
        }
      }

      const pagesResult = resolvedRunId
        ? await supabase
            .from("ocr_page_lifecycle")
            .select("*")
            .eq("flyer_scan_run_id", resolvedRunId)
            .order("page_num", { ascending: true })
            .order("attempt_num", { ascending: true })
        : { data: [] as any[] };
      const pages = (pagesResult.data ?? []) as PageLifecycleRow[];

      // Compute no-progress duration
      let noProgressSeconds = 0;
      if (runData?.last_progress_at) {
        noProgressSeconds = Math.floor(
          (Date.now() - new Date(runData.last_progress_at).getTime()) / 1000
        );
      }

      // Extract events from discovery_debug
      const events = extractEventsFromDebug(runData?.discovery_debug);

      // Classify health
      const { level, message } = classifyHealth(runData, noProgressSeconds, isRunning);

      setState({
        runData,
        pages,
        events,
        resolvedRunId,
        resolvedBy,
        health: level,
        healthMessage: message,
        noProgressSeconds,
        isLoading: false,
        lastFetchedAt: new Date().toISOString(),
      });
    } catch (err) {
      console.error("[CR320] S12 diagnostics fetch error:", err);
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  }, [flyerScanRunId, batchRunId, isRunning, s1OcrAttemptId, attemptStartedAt]);

  // Initial fetch + polling
  useEffect(() => {
    fetchData();

    if (isRunning) {
      const interval = setInterval(fetchData, 3000);
      return () => clearInterval(interval);
    }
  }, [fetchData, isRunning]);

  // Debug bundle builder
  const buildDebugBundle = useCallback((): string => {
    const { runData, pages, events } = state;
    if (!runData) return JSON.stringify({ error: "No run data" });

    const bundle = {
      _meta: {
        generated_at: new Date().toISOString(),
        run_id: runData.id,
        dashboard_version: "CR320-v1",
      },
      run: {
        id: runData.id,
        status: runData.status,
        current_page: runData.current_page,
        pages_total: runData.pages_total,
        current_phase: runData.current_phase,
        last_progress_at: runData.last_progress_at,
        watchdog_timeout_sec: runData.watchdog_timeout_sec,
        started_at: runData.started_at,
        completed_at: runData.completed_at,
        failure_code: runData.failure_code,
        failure_reason: runData.failure_reason,
        force_stop_requested: runData.force_stop_requested,
        force_skip_page_requested: runData.force_skip_page_requested,
        primary_ocr_engine: runData.primary_ocr_engine,
        counters: {
          pages_rendered: runData.pages_rendered,
          pages_ocr_completed: runData.pages_ocr_completed,
          pages_candidate: runData.pages_candidate,
          pages_ai_scanned: runData.pages_ai_scanned,
          deals_detected_count: runData.deals_detected_count,
        },
      },
      health: {
        level: state.health,
        message: state.healthMessage,
        no_progress_seconds: state.noProgressSeconds,
      },
      pages: pages.map((p) => ({
        page_num: p.page_num,
        attempt: p.attempt_num,
        profile: p.profile,
        engine: p.ocr_engine,
        status: p.status,
        duration_ms: p.duration_ms,
        render_ms: p.render_ms,
        ocr_ms: p.ocr_ms,
        text_len: p.text_len,
        avg_conf: p.avg_conf,
        error_code: p.error_code,
        error_message: p.error_message?.substring(0, 200),
      })),
      events: events.slice(-50),
      s12_debug: runData.discovery_debug?.s12
        ? {
            page: runData.discovery_debug.s12.page,
            stage: runData.discovery_debug.s12.stage,
            stage_started_at: runData.discovery_debug.s12.stage_started_at,
            attempt: runData.discovery_debug.s12.attempt,
            timings_ms: runData.discovery_debug.s12.timings_ms,
            last_error: runData.discovery_debug.s12.last_error,
          }
        : null,
    };

    return JSON.stringify(bundle, null, 2);
  }, [state]);

  // Computed summaries
  const pageStats = useMemo(() => {
    const { pages } = state;
    const errorPages = pages.filter((p) => p.status === "error" || p.error_code);
    const slowPages = [...pages]
      .filter((p) => p.ocr_ms != null)
      .sort((a, b) => (b.ocr_ms ?? 0) - (a.ocr_ms ?? 0))
      .slice(0, 10);

    const avgOcrMs =
      pages.length > 0
        ? Math.round(
            pages.reduce((sum, p) => sum + (p.ocr_ms ?? 0), 0) /
              pages.filter((p) => p.ocr_ms != null).length || 1
          )
        : 0;

    const errorsByType = new Map<string, number>();
    errorPages.forEach((p) => {
      const code = p.error_code || "unknown";
      errorsByType.set(code, (errorsByType.get(code) || 0) + 1);
    });

    return { errorPages, slowPages, avgOcrMs, errorsByType };
  }, [state.pages]);

  return {
    ...state,
    buildDebugBundle,
    pageStats,
    refetch: fetchData,
  };
}
